import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Col, Input, Label, Row } from "reactstrap";
import { compose } from "redux";
import { GrowerLogin } from "../../actions/auth/Auth";
import { getTenantLoginOptions } from "../../helpers/auth";
import { nzapTheme } from "../../helpers/themes";
import { updateTenantTheme } from "../../helpers/util";
import { useGetContext } from "../../hooks/useGetContext";
import "../../styles/GrowerLoginPage.scss";
import ToastButton from "../common/ToastButton";
import Footer from "./Footer";
import GrowerLoginLayout from "./GrowerLoginLayout";
import InvalidTenantPage from "./InvalidTenantPage";

const GrowerLoginPage: React.FC<IGrowerLoginPageProps> = (
  props: IGrowerLoginPageProps
) => {
  const history = useHistory();
  const params = useParams<{tenant: string}>();
  const { theme } = useGetContext();

  const [name, setName] = useState(() => props.auth.growerName ?? "");

  const tenantName = params.tenant;
  const validTenants = props.auth.validTenants;
  const isValidTenant = validTenants.includes(tenantName);

  useEffect(() => {
    // If the current theme is not equal to the theme to set.
    if (!isEqual(theme?.theme, nzapTheme)) {
      updateTenantTheme(theme, nzapTheme, tenantName);
    }
  }, [theme, tenantName]);

  useEffect(() => {
    if (props.auth.isAuthenticated && !props.auth.loading) {
      history.push("/");
    }
  }, [history, props.auth]);

  const doLogin = async () => {
    try {
      const loginOptions = getTenantLoginOptions(tenantName);
      await props.GrowerLogin(loginOptions, name.trim());
    } catch (err) {
      toast.error(
        (err as Error).message || (
          <p className="p-0 m-0">
            An unknown error has occurred. If this happens again please contact
            support. <b>(Error code: TA1030)</b>
          </p>
        ),
        {
          toastId: 1,
          autoClose: false,
          closeButton: <ToastButton label="Dismiss" />,
        }
      );
    }
  };

  if (isValidTenant) {
    return (
      <GrowerLoginLayout tenant={tenantName}>
        <>
          <Row className="mb-3">
            <Col xs={12}>
              <img
                src={`https://${tenantName}-metwatch-assets.s3.ap-southeast-2.amazonaws.com/trappa-logo-2025.png`}
                alt="Trappa Logo"
                style={{
                  height: "48px",
                }}
              />
            </Col>
          </Row>
          <Row style={{ width: "80%" }}>
            <Col xs={12}>
              <h5 className="text-center font-weight-light">
                Codling Moth Sensitive Market Trap Monitoring App
              </h5>
            </Col>
          </Row>
          <Row className="login-container">
            <Col xs={12} className='d-flex flex-column'>
              <div className="mb-3">
                <Label for="grower-name">
                  Operator Name
                </Label>
                <Input
                  id="grower-name"
                  name="grower-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <Button
                  className={`login-btn-${tenantName}`}
                  onClick={doLogin}
                  disabled={props.auth.loading || name.trim() === ""}
                >
                  {props.auth.loading ? "Authenticating..." : "Start Monitoring"}
                </Button>
              </div>
            </Col>
          </Row>
          <Footer tenantName={tenantName} />
        </>
      </GrowerLoginLayout>
    );
  }

  return (
    <GrowerLoginLayout>
      <InvalidTenantPage />
    </GrowerLoginLayout>
  );
};

const mapStateToProps = ({ auth }: { auth: IAuthReducer }) => {
	return { auth };
};

export default compose<React.ComponentType<Partial<IGrowerLoginPageProps>>>(
	connect(mapStateToProps, { GrowerLogin })
)(GrowerLoginPage);
